import { useBlocker } from "react-router-dom";
import { createContext, useContext, useState, useEffect } from "react";
import {
  BookingFlowContextData,
  BookingFlowProviderProps,
  BookingFlowStatus,
  DefaultFlowStep,
  ErrorMessages,
  FlowStep,
  FlowTransitions,
  FlowType,
  ProcessingStatus,
  UpsellFlowStep,
  simulatedPayment,
  simulatedUpdatedCycles,
  SimulatedUpsell,
  SimulatedUpsellResponse,
  SimulateUpsellProps,
  PoContractCreateData,
  DefaultPoContractFlowStep,
  UpsellPoContractFlowStep,
} from "./use-booking-flow.types";
import { CreateBookingDataParams } from "../../adapters/create-booking/create-booking.types";
import { apiWobaBackend } from "@/services/api-woba-backend";

const BookingFlowContext = createContext<BookingFlowContextData>(
  {} as BookingFlowContextData,
);

export function BookingFlowProvider({ children }: BookingFlowProviderProps) {
  const [flowType, setFlowType] = useState<FlowType>("default");
  const [currentStep, setCurrentStep] = useState<FlowStep>(
    DefaultFlowStep.CREATE,
  );
  const [bookingData, setBookingData] = useState<any>(null);
  const [upsellData, setUpsellData] = useState<any>(null);
  const [selectedCycle, setSelectedCycle] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [selectedCompany, setSelectedCompany] = useState<{ id: number } | null>(
    null,
  );
  const [selectedPackageGroups, setSelectedPackageGroups] = useState<
    { name: string }[]
  >([]);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    canNavigateAway: true,
    currentOperation: null,
  });
  const [bookingFlowStatus, setBookingFlowStatus] = useState<BookingFlowStatus>(
    {
      booking: null,
      upsell: null,
      poContract: null,
    },
  );
  const [simulatedUpsell, setSimulatedUpsell] =
    useState<SimulatedUpsell | null>(upsellData?.simulatedUpsellData ?? null);

  const [poContractCreateData, setPoContractCreateData] =
    useState<PoContractCreateData>();

  const UpdateUpsellPayments = (payments: simulatedPayment[]) => {
    setSimulatedUpsell((prev) =>
      prev ? { ...prev, newPayments: payments } : prev,
    );
  };

  const UpdateUpsellCycles = (cycles: simulatedUpdatedCycles[]) => {
    setSimulatedUpsell((prev) =>
      prev ? { ...prev, updatedCycles: cycles } : prev,
    );
  };

  const SimulateUpsell = async ({
    creditAmount,
    beginDate,
    companyId,
    packageId,
    nativeProrata,
  }: SimulateUpsellProps) => {
    const response: SimulatedUpsellResponse = await apiWobaBackend.post(
      `/companies/${companyId}/packages/${packageId}/simulate-upsell`,
      {
        creditAmount,
        beginDate,
        ...(nativeProrata ? { nativeProrata: true } : {}),
      },
    );

    setSimulatedUpsell(response.data);
  };

  const handleProcessingStatus = (status: Partial<ProcessingStatus>) => {
    setProcessingStatus((prev) => ({ ...prev, ...status }));
  };

  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});

  const updateBookingFlowStatus = (status: Partial<BookingFlowStatus>) => {
    setBookingFlowStatus((prev) => ({ ...prev, ...status }));
  };

  const updateErrorMessages = (messages: Partial<ErrorMessages>) => {
    setErrorMessages((prev) => ({ ...prev, ...messages }));
  };

  const nextStep = () => {
    const transition = FlowTransitions[currentStep];
    console.log({ transition });
    setCurrentStep(transition.next);
  };

  const previousStep = () => {
    const transition = FlowTransitions[currentStep];
    if (transition.previous) {
      setCurrentStep(transition.previous);
    }
  };

  const resetFlow = () => {
    setFlowType("default");
    setCurrentStep(DefaultFlowStep.CREATE);
    setBookingData(null);
    setPoContractCreateData(undefined);
    setUpsellData(null);
    setSelectedCycle(null);
    setSelectedPackage(null);
    setBookingFlowStatus({ booking: null, upsell: null, poContract: null });
    setErrorMessages({});
  };

  const moveToSummaryStep = () => {
    const nextStep =
      flowType === "default" ? DefaultFlowStep.SUMMARY : UpsellFlowStep.SUMMARY;
    setCurrentStep(nextStep);
  };

  const handleSubmitBookingData = (data: CreateBookingDataParams) => {
    setBookingData(data);
    setFlowType(data.flow_type);
    const isUpsell = data.flow_type === "upsell";
    const nextStep = isUpsell ? UpsellFlowStep.UPSELL : DefaultFlowStep.SUMMARY;
    setCurrentStep(nextStep);
  };

  const handleSubmitPoContractData = (data: PoContractCreateData) => {
    setPoContractCreateData(data);
    setFlowType(data.flowType);
    const isUpsell = data.flowType === "upsell";
    const nextStep = isUpsell
      ? UpsellPoContractFlowStep.UPSELL
      : DefaultPoContractFlowStep.SUMMARY;
    setCurrentStep(nextStep);
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      processingStatus.isProcessing &&
      !processingStatus.canNavigateAway &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  // blocker to prevent navigation away from the page while processing
  useEffect(() => {
    if (blocker.state === "blocked") {
      const shouldNavigate = window.confirm(
        "Operação em andamento. Deseja realmente sair?",
      );
      if (shouldNavigate) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // blocker to prevent external navigation while processing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (processingStatus.isProcessing && !processingStatus.canNavigateAway) {
        e.preventDefault();
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [processingStatus]);

  return (
    <BookingFlowContext.Provider
      value={{
        flowType,
        setFlowType,
        currentStep,
        setCurrentStep,
        bookingData,
        upsellData,
        poContractCreateData,
        setBookingData,
        setUpsellData,
        setPoContractCreateData,
        nextStep,
        previousStep,
        resetFlow,
        bookingFlowStatus,
        handleSubmitBookingData,
        handleSubmitPoContractData,
        SimulateUpsell,
        setSimulatedUpsell,
        simulatedUpsell,
        setSelectedCycle,
        selectedCycle,
        setSelectedPackage,
        selectedPackage,
        setSelectedPackageGroups,
        selectedPackageGroups,
        setSelectedCompany,
        selectedCompany,
        UpdateUpsellPayments,
        UpdateUpsellCycles,
        setBookingFlowStatus: updateBookingFlowStatus,
        errorMessages,
        setErrorMessages: updateErrorMessages,
        processingStatus,
        setProcessingStatus: handleProcessingStatus,
        moveToSummaryStep,
      }}
    >
      {children}
    </BookingFlowContext.Provider>
  );
}

export const useBookingFlow = () => {
  const context = useContext(BookingFlowContext);
  if (!context) {
    throw new Error("useBookingFlow must be used within a BookingFlowProvider");
  }
  return context;
};
